// §6.6: Supply log review — billing team assigns revenue codes to CDI-detected and manual supplies.
import { updateEncounterBillingFields } from '@backend/functions/gyn'
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useApp, useAuth, useCollection, usePopUpMessage } from '@hb/react'
import {
  BillingSupplyAssignment,
  Encounter,
  ManualBillingSupplyEntry,
  RevenueCodeEntry,
  SupplyLogEntry,
  revenueCodesCollection,
} from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'

// ─── Revenue code selector ────────────────────────────────────────────────────

const RevenueCodeSelect = ({
  value,
  onChange,
  revenueCodes,
}: {
  value: string
  onChange: (code: string) => void
  revenueCodes: (RevenueCodeEntry & { id: string })[]
}) => (
  <Select
    size="xs"
    placeholder="Select revenue code"
    value={value}
    onChange={e => onChange(e.target.value)}
    maxW="220px"
    fontSize="xs">
    {revenueCodes.map(rc => (
      <option key={rc.id} value={rc.code}>
        {rc.code} — {rc.description}
      </option>
    ))}
  </Select>
)

// ─── CDI entry row (confirmed) ────────────────────────────────────────────────

const ConfirmedEntryRow = ({
  entry,
  assignment,
  revenueCodes,
  onAssign,
}: {
  entry: SupplyLogEntry
  assignment: BillingSupplyAssignment | undefined
  revenueCodes: (RevenueCodeEntry & { id: string })[]
  onAssign: (entryId: string, revenueCode: string) => void
}) => (
  <HStack
    key={entry.id}
    justify="space-between"
    align="center"
    px={2}
    py={1}
    borderRadius={4}
    bg="green.50"
    border="1px solid"
    borderColor="green.200">
    <Box flex={1} minW={0}>
      <Text fontSize="sm" color="green.800" fontWeight={500} noOfLines={1}>
        {entry.matchedSupply}
      </Text>
      {entry.hcpcsCode ? (
        <Text fontSize="xs" color="gray.500">
          HCPCS: {entry.hcpcsCode}
        </Text>
      ) : null}
    </Box>
    <HStack spacing={2} flexShrink={0}>
      <RevenueCodeSelect
        value={assignment?.revenueCode ?? ''}
        onChange={code => onAssign(entry.id, code)}
        revenueCodes={revenueCodes}
      />
      {assignment?.revenueCode ? (
        <Badge colorScheme="green" fontSize="xs">
          Assigned
        </Badge>
      ) : (
        <Badge colorScheme="orange" fontSize="xs">
          Needs code
        </Badge>
      )}
    </HStack>
  </HStack>
)

// ─── Manual supply entry form ─────────────────────────────────────────────────

const EMPTY_MANUAL: Omit<ManualBillingSupplyEntry, 'id' | 'addedBy' | 'addedOn'> = {
  supplyName: '',
  hcpcsCode: '',
  revenueCode: '',
  quantity: undefined,
  notes: '',
}

const ManualEntryForm = ({
  revenueCodes,
  onSave,
  onCancel,
}: {
  revenueCodes: (RevenueCodeEntry & { id: string })[]
  onSave: (entry: Omit<ManualBillingSupplyEntry, 'id' | 'addedBy' | 'addedOn'>) => void
  onCancel: () => void
}) => {
  const [form, setForm] = useState(EMPTY_MANUAL)
  const set = (field: keyof typeof EMPTY_MANUAL, value: string | number | undefined) =>
    setForm(prev => ({ ...prev, [field]: value }))

  return (
    <VStack
      align="stretch"
      spacing={2}
      px={3}
      py={2}
      borderRadius={4}
      bg="blue.50"
      border="1px solid"
      borderColor="blue.200">
      <Text fontSize="xs" fontWeight={600} color="blue.700">
        ADD MANUAL SUPPLY ENTRY
      </Text>
      <HStack spacing={2}>
        <Input
          size="xs"
          placeholder="Supply / medication name"
          value={form.supplyName}
          onChange={e => set('supplyName', e.target.value)}
          flex={1}
        />
        <Input
          size="xs"
          placeholder="HCPCS code"
          value={form.hcpcsCode ?? ''}
          onChange={e => set('hcpcsCode', e.target.value)}
          maxW="120px"
        />
        <Input
          size="xs"
          placeholder="Qty"
          type="number"
          value={form.quantity ?? ''}
          onChange={e => set('quantity', e.target.value ? Number(e.target.value) : undefined)}
          maxW="60px"
        />
      </HStack>
      <HStack spacing={2}>
        <RevenueCodeSelect
          value={form.revenueCode ?? ''}
          onChange={code => set('revenueCode', code)}
          revenueCodes={revenueCodes}
        />
      </HStack>
      <Textarea
        size="xs"
        placeholder="Notes (optional)"
        value={form.notes ?? ''}
        onChange={e => set('notes', e.target.value)}
        rows={2}
        resize="none"
      />
      <HStack justify="flex-end" spacing={2}>
        <Button size="xs" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="xs"
          colorScheme="blue"
          isDisabled={!form.supplyName.trim()}
          onClick={() => onSave(form)}>
          Add Supply
        </Button>
      </HStack>
    </VStack>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export const SupplyLogReview = ({
  encounter,
  examId,
}: {
  encounter: Encounter
  examId: string
}) => {
  const { authUser } = useAuth()
  const { appName } = useApp()
  const { showError } = usePopUpMessage()
  const revenueCodesData = useCollection(revenueCodesCollection)
  const revenueCodes = useMemo(
    () =>
      revenueCodesData
        ? (Object.entries(revenueCodesData).map(([id, rc]) => ({
            ...rc,
            id,
          })) as (RevenueCodeEntry & { id: string })[])
        : [],
    [revenueCodesData],
  )

  const supplyLog = encounter.supplyLog ?? []
  const confirmed = supplyLog.filter(e => e.confirmedByProvider)
  const dismissed = supplyLog.filter(e => e.dismissedByProvider)
  const pending = supplyLog.filter(e => !e.confirmedByProvider && !e.dismissedByProvider)
  const manualEntries = useMemo(
    () => encounter.manualBillingSupplies ?? [],
    [encounter.manualBillingSupplies],
  )
  const assignments = useMemo(
    () => encounter.billingSupplyAssignments ?? {},
    [encounter.billingSupplyAssignments],
  )

  const [showAddForm, setShowAddForm] = useState(false)

  // Firestore rules allow no client writes to ENCOUNTERS — billing metadata is persisted via
  // the admin-only updateEncounterBillingFields callable; errors surface instead of failing silently.
  const handleAssign = useCallback(
    async (entryId: string, revenueCode: string) => {
      if (!authUser) return
      const updated: Record<string, BillingSupplyAssignment> = {
        ...assignments,
        [entryId]: {
          revenueCode,
          assignedBy: authUser.uid,
          assignedOn: Date.now(),
        },
      }
      try {
        await updateEncounterBillingFields({
          encounterId: examId,
          billingSupplyAssignments: updated,
          appName,
        })
      } catch (err) {
        showError((err as Error)?.message ?? 'Could not save the revenue code assignment.')
      }
    },
    [authUser, assignments, examId, appName, showError],
  )

  const handleAddManual = useCallback(
    async (entry: Omit<ManualBillingSupplyEntry, 'id' | 'addedBy' | 'addedOn'>) => {
      if (!authUser) return
      const newEntry: ManualBillingSupplyEntry = {
        ...entry,
        id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        addedBy: authUser.uid,
        addedOn: Date.now(),
      }
      const updated = [...manualEntries, newEntry]
      try {
        await updateEncounterBillingFields({
          encounterId: examId,
          manualBillingSupplies: updated,
          appName,
        })
        setShowAddForm(false)
      } catch (err) {
        showError((err as Error)?.message ?? 'Could not add the supply entry.')
      }
    },
    [authUser, manualEntries, examId, appName, showError],
  )

  const handleRemoveManual = useCallback(
    async (id: string) => {
      const updated = manualEntries.filter(e => e.id !== id)
      try {
        await updateEncounterBillingFields({
          encounterId: examId,
          manualBillingSupplies: updated,
          appName,
        })
      } catch (err) {
        showError((err as Error)?.message ?? 'Could not remove the supply entry.')
      }
    },
    [manualEntries, examId, appName, showError],
  )

  const totalConfirmed = confirmed.length + manualEntries.length
  const assignedCount =
    confirmed.filter(e => assignments[e.id]?.revenueCode).length +
    manualEntries.filter(e => e.revenueCode).length

  return (
    <VStack align="stretch" spacing={2} w="100%">
      {/* Summary line */}
      <HStack justify="space-between">
        <Text fontSize="xs" color="gray.500">
          {totalConfirmed === 0
            ? 'No billable supplies on this encounter.'
            : `${assignedCount} of ${totalConfirmed} supplies have revenue codes assigned.`}
        </Text>
        {totalConfirmed > 0 && assignedCount < totalConfirmed ? (
          <Badge colorScheme="orange" fontSize="xs">
            Incomplete
          </Badge>
        ) : totalConfirmed > 0 ? (
          <Badge colorScheme="green" fontSize="xs">
            Complete
          </Badge>
        ) : null}
      </HStack>

      {/* CDI-confirmed entries with revenue code dropdowns */}
      {confirmed.length > 0 && (
        <VStack align="stretch" spacing={1}>
          <Text fontSize="xs" color="gray.400" fontWeight={500}>
            CDI-CONFIRMED SUPPLIES
          </Text>
          {confirmed.map(entry => (
            <ConfirmedEntryRow
              key={entry.id}
              entry={entry}
              assignment={assignments[entry.id]}
              revenueCodes={revenueCodes}
              onAssign={handleAssign}
            />
          ))}
        </VStack>
      )}

      {/* Manually added entries */}
      {manualEntries.length > 0 && (
        <VStack align="stretch" spacing={1}>
          <Text fontSize="xs" color="gray.400" fontWeight={500}>
            MANUALLY ADDED
          </Text>
          {manualEntries.map(entry => (
            <HStack
              key={entry.id}
              justify="space-between"
              align="center"
              px={2}
              py={1}
              borderRadius={4}
              bg="blue.50"
              border="1px solid"
              borderColor="blue.200">
              <Box flex={1} minW={0}>
                <Text fontSize="sm" color="blue.800" fontWeight={500} noOfLines={1}>
                  {entry.supplyName}
                </Text>
                <HStack spacing={2}>
                  {entry.hcpcsCode ? (
                    <Text fontSize="xs" color="gray.500">
                      HCPCS: {entry.hcpcsCode}
                    </Text>
                  ) : null}
                  {entry.quantity ? (
                    <Text fontSize="xs" color="gray.500">
                      Qty: {entry.quantity}
                    </Text>
                  ) : null}
                </HStack>
              </Box>
              <HStack spacing={2} flexShrink={0}>
                {entry.revenueCode ? (
                  <Badge colorScheme="green" fontSize="xs">
                    {entry.revenueCode}
                  </Badge>
                ) : (
                  <Badge colorScheme="orange" fontSize="xs">
                    No code
                  </Badge>
                )}
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleRemoveManual(entry.id)}>
                  ✕
                </Button>
              </HStack>
            </HStack>
          ))}
        </VStack>
      )}

      {/* Add manual entry */}
      {showAddForm ? (
        <ManualEntryForm
          revenueCodes={revenueCodes}
          onSave={handleAddManual}
          onCancel={() => setShowAddForm(false)}
        />
      ) : (
        <Button
          size="xs"
          variant="outline"
          colorScheme="blue"
          alignSelf="flex-start"
          onClick={() => setShowAddForm(true)}>
          + Add Supply Manually
        </Button>
      )}

      {/* Read-only: dismissed + pending CDI */}
      {(dismissed.length > 0 || pending.length > 0) && (
        <>
          <Divider />
          <VStack align="stretch" spacing={0.5}>
            <Text fontSize="xs" color="gray.400" fontWeight={500}>
              OTHER CDI DETECTIONS
            </Text>
            {dismissed.map(e => (
              <HStack key={e.id} px={1} justify="space-between">
                <Text fontSize="xs" color="gray.400" textDecoration="line-through">
                  {e.matchedSupply}
                </Text>
                <Badge colorScheme="gray" fontSize="xs">
                  Dismissed
                </Badge>
              </HStack>
            ))}
            {pending.map(e => (
              <HStack key={e.id} px={1} justify="space-between">
                <Text fontSize="xs" color="orange.600">
                  {e.matchedSupply}
                </Text>
                <Badge colorScheme="orange" fontSize="xs">
                  Pending provider
                </Badge>
              </HStack>
            ))}
          </VStack>
        </>
      )}
    </VStack>
  )
}

// Re-export the pure utility from @hb/shared so callers can import from this module.
export { isSupplyLogReviewComplete } from '@hb/shared'
